import type { ICellRendererParams } from 'ag-grid-community';
import type { PromptExecutionHistoryRow } from '../types';
import { ModelIcon } from '../components/ModelIcon';
import { StatusBadge } from '../components/MetricsCards';
import { formatUtcDate } from '@/helpers/formatUtcDate';
import { formatExecutionVisibility } from '../utils/execution-filters';
import { getHelpHtml } from '../utils/help/help-content';

function columnHelp(key: string): string | undefined {
  return getHelpHtml(key) || undefined;
}

function PositionCell({ position }: { position: number | null }) {
  if (position == null) {
    return <span className="text-300">—</span>;
  }
  return <span className="badge rounded-pill bg-primary text-white">#{position}</span>;
}

export interface PromptHistoryRunsColumnCallbacks {
  competitorColumns: string[];
  onViewResponse: (row: PromptExecutionHistoryRow) => void;
  loadingResponseId?: string | null;
}

export function promptHistoryRunsColumnConfig(callbacks: PromptHistoryRunsColumnCallbacks) {
  const cols: Array<Record<string, unknown>> = [
    {
      field: 'executedAt',
      title: 'زمان اجرا',
      tooltip: columnHelp('tables.executed_at'),
      type: 'string',
      width: 150,
      searchable: true,
      valueFormatter: (p: { value: string }) => formatUtcDate(p.value, { preset: 'dateTime' }) || '—',
    },
    {
      field: 'modelName',
      title: 'مدل',
      tooltip: columnHelp('tables.model'),
      type: 'string',
      width: 120,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptExecutionHistoryRow>) => {
        const row = params.data;
        if (!row?.modelName) return <span>—</span>;
        return (
          <span className="d-inline-flex align-items-center gap-2" title={row.modelName}>
            <ModelIcon svg={row.modelIconSvg} name={row.modelName} size={20} />
          </span>
        );
      },
    },
    {
      field: 'ownPosition',
      title: 'برند شما',
      tooltip: columnHelp('tables.your_brand'),
      type: 'string',
      width: 110,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptExecutionHistoryRow>) =>
        params.data ? <PositionCell position={params.data.ownPosition} /> : null,
    },
  ];

  for (const name of callbacks.competitorColumns) {
    cols.push({
      field: `_competitor_${name}`,
      title: name,
      type: 'string',
      width: 110,
      sortable: false,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<PromptExecutionHistoryRow>) => {
        const row = params.data;
        if (!row) return null;
        const cell = row.competitors.find((competitor) => competitor.name === name);
        return <PositionCell position={cell?.position ?? null} />;
      },
    });
  }

  cols.push(
    {
      field: 'visibilityScore',
      title: 'دیده‌شدن',
      tooltip: columnHelp('metrics.ai_visibility'),
      type: 'string',
      width: 110,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptExecutionHistoryRow>) =>
        params.data ? (
          <span className="fw-semibold text-primary">{formatExecutionVisibility(params.data)}</span>
        ) : null,
    },
    {
      field: 'status',
      title: 'وضعیت',
      tooltip: columnHelp('tables.status'),
      type: 'string',
      width: 120,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptExecutionHistoryRow>) =>
        params.data ? <StatusBadge status={params.data.status} /> : null,
    },
    {
      field: '_actions',
      title: 'عملیات',
      tooltip: columnHelp('tables.actions'),
      type: 'string',
      width: 130,
      sortable: false,
      searchable: false,
      pinned: 'left',
      lockPosition: true,
      cellRenderer: (params: ICellRendererParams<PromptExecutionHistoryRow>) => {
        const row = params.data;
        if (!row) return null;
        const loading = callbacks.loadingResponseId === row.id;
        return (
          <button
            type="button"
            className="btn btn-sm btn-falcon-default py-0 px-2 fs--2"
            disabled={loading}
            onMouseDown={e => e.stopPropagation()}
            onClick={e => {
              e.stopPropagation();
              callbacks.onViewResponse(row);
            }}
          >
            {loading ? '...' : 'پاسخ پرامپت'}
          </button>
        );
      },
    },
  );

  return cols;
}
